import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash.debounce';
import { showToast } from '../../shared/Toast';
import { useForm } from 'react-hook-form';
import Tooltip from '../../shared/Tooltip';
import { Undo, Redo, ChevronDownIcon, PencilLineIcon, XIcon, ChevronUp, ChevronDown, Trash2, AlertTriangle } from 'lucide-react';
import {
  OptionProps,
  ProjectModel,
  SbCurrentUser,
  SbStatusMaster,
  SbStatusMasterKeyValue,
  SubmissionBoardModel,
  getDateString,
  isExpired,
} from '../component/SbModel';
import { Dialog, Transition } from '@headlessui/react';
import { Participant } from '../../search/ParticipantsCombobox';
import { filterProjectMembersBySearchTerm } from '../../calendar/eventFormUtils';
import submissionBoardService from '../../services/SubmissionBoardService';
import SelectCombobox from '../component/SelectCombobox';
import SelectComboboxSimple from '../component/SelectComboboxSimple';
import ParticipantItem from '../../search/ParticipantItem';
import projectService from '../../services/ProjectService';
import Button from '../component/Button';
import MultiSelectInput from '../component/MultiSelectInput';
import userService from '../../services/UserService';
import ConfirmationDialog from '../../shared/ConfirmationDialog';

export interface SbAddProps {
  currentUser: SbCurrentUser;
  project: ProjectModel;
  submissionBoardModel: SubmissionBoardModel;
  dialogCloseHandler?: (open: boolean, postData: any) => void;
}
export interface ParticipantOpr extends Participant {
  sb_recipient_id?: number; // undefined : new: value:edit
  action?: number; // undefined or 0: nothing, 1:add, 2=delete
}
export default function SbAdd({
  currentUser,
  project,
  submissionBoardModel,
  dialogCloseHandler,
}: SbAddProps) {
  const [open, setOpen] = useState(true);
  const [isHeaderEdit, setIsHeaderEdit] = useState(false);
  const [is_multiple_hire, setis_multiple_hire] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState<ParticipantOpr[]>([]);
  const [previousRecipients, setPreviousRecipients] = useState<ParticipantOpr[]>([]);
  const [recipientMembers, setRecipientMembers] = useState([]);
  const [isSbLocked, setIsSbLocked] = useState(false);
  const [projectSceneCards, setProjectSceneCards] = useState<OptionProps[]>([]);
  const [projectOwners, setProjectOwners] = useState<OptionProps[]>([]);
  const [projectEpisodes, setProjectEpisodes] = useState<OptionProps[]>([]);
  const inputHeaderRef = useRef(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Undo/Redo state for Requirements field
  const [requirementsHistory, setRequirementsHistory] = useState<string[]>([]);
  const [requirementsHistoryIndex, setRequirementsHistoryIndex] = useState<number>(-1);
  const isUndoRedoAction = useRef(false);

  useEffect(() => {
    if (isHeaderEdit) {
      setFocus('header');
      // setTimeout(() => {
      //   if (inputHeaderRef && inputHeaderRef.current) {
      //     inputHeaderRef.current.focus();
      //   }
      // }, 2000);
    }
  }, [isHeaderEdit]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // On Enter, save the new value and exit edit mode
      e.preventDefault();
      setIsHeaderEdit(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // On Escape, revert to the original value and exit edit mode
      // setValue('sb_status', selectedValue.id);
      setIsHeaderEdit(false);
    }
  };

  const getTodayDateString = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const getTomorrowDateString = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // Add one day
    const year = tomorrow.getFullYear();
    const month = (tomorrow.getMonth() + 1).toString().padStart(2, '0');
    const day = tomorrow.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const onCloseDialog = (value: boolean) => {
    dialogCloseHandler(value, null);
  };
  const handleFormSubmit = async (data) => {
    try {
      console.log('data', data);
      if (data.title == '') {
        showToast('Invalid Data: Title is required', 'danger');
        return;
      }
      if (data.requirements == '') {
        showToast('Invalid Data: Requirement is required', 'danger');
        return;
      }
      if (data.expire_at == '') {
        showToast('Invalid Data: Expire at is required', 'danger');
        return;
      }
      if (is_multiple_hire && data.multiple_hire <= 1) {
        showToast('Invalid Data: Multiple hire set value min to 2.', 'danger');
        return;
      }
      const sb_recipients = selectedParticipants.map((item, index) => {
        return {
          recipient_email: item.email,
          user_id: item.name == undefined || item.name == '' ? null : item.id,
          sb_recipient_id:
            item.sb_recipient_id == undefined || item.sb_recipient_id <= 0
              ? 0
              : item.sb_recipient_id,
          action: item.action == undefined ? 0 : item.action,
        };
      });
      let postData = {
        ...data,
        ...{ sb_recipients: sb_recipients, multiple_hire: is_multiple_hire ? data.multiple_hire : 0 },
      };

      // Ensure owner_id is set correctly (defaults to current user if not selected)
      if (!postData.owner_id) {
        postData.owner_id = currentUser.id;
      }

      const response = await submissionBoardService.createSubmissionDocument(
        project,
        submissionBoardModel,
        postData
      );
      if (submissionBoardModel.id == undefined || submissionBoardModel.id <= 0) {
        showToast('Successfully created ' + postData.title, 'success');
        dialogCloseHandler(false, response);
      } else {
        showToast('Successfully updated ' + postData.title, 'success');
        dialogCloseHandler(false, response);
      }
    } catch (error) {
      showToast('Error in creating Submission Board', 'danger');
    }
  };

  const handleDeleteSubmissionBoard = () => {
    if (!submissionBoardModel.id || submissionBoardModel.id <= 0) {
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteSubmissionBoard = async () => {
    try {
      setIsDeleting(true);
      setShowDeleteConfirmation(false);
      await submissionBoardService.deleteSubmissionBoard(project.uuid, submissionBoardModel.id);
      showToast('Submission board deleted successfully', 'success');
      dialogCloseHandler(false, { deleted: true, id: submissionBoardModel.id });
    } catch (error) {
      showToast('Error deleting submission board', 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  let currentDate = new Date();
  const {
    control,
    watch,
    getValues,
    setFocus,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SubmissionBoardModel>({
    defaultValues: {
      title: submissionBoardModel.title,
      header: submissionBoardModel?.header ?? '',
      requirements: submissionBoardModel.requirements ?? '',
      project_id: submissionBoardModel.project_id,
      project_folder_uuid: submissionBoardModel?.project_folder_uuid ?? '',
      scene_card_uuid: submissionBoardModel?.scene_card_uuid ?? '',
      multiple_hire: submissionBoardModel.multiple_hire,
      is_multiple_hire: (submissionBoardModel?.multiple_hire ?? 0) > 0 ? true : false,
      sb_status: submissionBoardModel.sb_status,
      is_recipient_otp: submissionBoardModel.is_recipient_otp ?? false,
      expire_at:
        submissionBoardModel.expire_at && submissionBoardModel.expire_at != ''
          ? getDateString(submissionBoardModel.expire_at, 'yyyy-MM-dd')
          : getDateString(currentDate, 'yyyy-MM-dd'),
      owner_id: submissionBoardModel.owner_id || currentUser.id,
      created_by: submissionBoardModel.created_by || currentUser.id,
    },
  });

  // Initialize requirements history
  useEffect(() => {
    const initialRequirements = submissionBoardModel.requirements ?? '';
    setRequirementsHistory([initialRequirements]);
    setRequirementsHistoryIndex(0);
  }, []);

  // Handle requirements change
  const handleRequirementsChange = (value: string) => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    const newHistory = requirementsHistory.slice(0, requirementsHistoryIndex + 1);
    newHistory.push(value);
    setRequirementsHistory(newHistory);
    setRequirementsHistoryIndex(newHistory.length - 1);
  };

  // Undo handler
  const handleUndo = () => {
    if (requirementsHistoryIndex > 0) {
      const newIndex = requirementsHistoryIndex - 1;
      isUndoRedoAction.current = true;
      setValue('requirements', requirementsHistory[newIndex]);
      setRequirementsHistoryIndex(newIndex);
    }
  };

  // Redo handler
  const handleRedo = () => {
    if (requirementsHistoryIndex < requirementsHistory.length - 1) {
      const newIndex = requirementsHistoryIndex + 1;
      isUndoRedoAction.current = true;
      setValue('requirements', requirementsHistory[newIndex]);
      setRequirementsHistoryIndex(newIndex);
    }
  };

  const canUndo = requirementsHistoryIndex > 0;
  const canRedo = requirementsHistoryIndex < requirementsHistory.length - 1;

  // Handle increment/decrement for multiple_hire
  const handleIncrement = () => {
    const currentValue = Number(getValues().multiple_hire) || 2;
    setValue('multiple_hire', currentValue + 1);
  };

  const handleDecrement = () => {
    const currentValue = Number(getValues().multiple_hire) || 2;
    if (currentValue > 2) {
      setValue('multiple_hire', currentValue - 1);
    }
  };

  useEffect(() => {
    const fetchRecipientForSB = async () => {
      try {
        if (submissionBoardModel.id == undefined || submissionBoardModel.id <= 0) {
          return;
        }
        const sb_recipients = await submissionBoardService.getSbRecipients(
          project.uuid,
          submissionBoardModel.id
        );
        if (sb_recipients && sb_recipients.length > 0) {
          sb_recipients.map((item) => {
            item.action = 0;
          });
          setPreviousRecipients(sb_recipients);
          setSelectedParticipants(sb_recipients);
        } else {
          setPreviousRecipients([]);
          setSelectedParticipants([]);
        }
      } catch (error) {
        showToast('Error while getting submission boards.', 'danger');
      } finally {
      }
    };
    async function fetchProjectMembers() {
      try {
        const users = await submissionBoardService.getProjectRecipients(project.uuid);
        setRecipientMembers(users);
      } catch (error) {
        showToast('Error while getting recipients.', 'danger');
      }
    }
    async function fetchProjectOwners() {
      try {
        const users = await submissionBoardService.getProjectOwners(project.uuid);
        if (users) {
          let loadedOwners = users.map(u => ({ id: u.id, name: u.name, role: u.role }));

          // Ensure current user is in the list if they are the creator (for existing board) or default (for new board)
          // This handles cases where the current user might not match availability filters but is the owner
          const currentUserId = currentUser.id;
          const isCurrentUserInList = loadedOwners.some(u => u.id === currentUserId);
          const targetOwnerId = getValues().owner_id || submissionBoardModel.owner_id || currentUserId;

          if (!isCurrentUserInList && targetOwnerId === currentUserId) {
            loadedOwners.push({
              id: currentUser.id,
              name: `${currentUser.first_name} ${currentUser.last_name}`,
              role: currentUser.role
            });
          }

          setProjectOwners(loadedOwners);

          // If creating new board, set default owner and role
          if ((submissionBoardModel.id == undefined || submissionBoardModel.id <= 0) && !getValues().owner_id) {
            const defaultOwnerId = currentUser.id;
            // Check if current user is in the list
            const currentUserOwner = loadedOwners.find(u => u.id === defaultOwnerId);
            if (currentUserOwner) {
              setValue('owner_id', defaultOwnerId);
            } else if (loadedOwners.length > 0) {
              // Fallback to first available owner if current user is not eligible
              setValue('owner_id', loadedOwners[0].id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching project owners:', error);
        // showToast('Error while getting owners.', 'danger');
      }
    }
    async function fetchProjectSceneCards() {
      try {
        const data = await projectService.fetchProjectFolders(
          project.uuid,
          'modules=episode,scene_cards,block&mod_block=1&mod_episode=1&mod_scene_cards=1'
        );
        const { scene_cards } = data;
        const { episodes } = data;
        const { blocks } = data;

        if (scene_cards) {
          setProjectSceneCards(
            scene_cards.map((item) => {
              return {
                id: item.uuid,
                name: item.scene_name,
              };
            })
          );
        }
        if (project.project_type == 'episodic') {
          if (blocks && blocks.length > 0) {
            let blocksItems = [];
            blocksItems.push({
              id: '',
              name: 'Core Team',
            });
            let blocksItemsAll = blocks.map((item) => {
              return {
                id: item.uuid,
                name: item.name + (item.block_number ? ' ' + item.block_number : ''),
              };
            })
            blocksItems = [...blocksItems, ...blocksItemsAll]

            setProjectEpisodes(blocksItems);
          } else {
            let blocksItems = [];
            blocksItems.push({
              id: '',
              name: 'Core Team',
            });
            let blocksItemsAll = episodes.map((item) => {
              return {
                id: item.uuid,
                name: item.name + (item.episode_number ? ' ' + item.episode_number : ''),
              };
            })
            blocksItems = [...blocksItems, ...blocksItemsAll]
            setProjectEpisodes(blocksItems);
          }
        }
      } catch (error) {
        showToast('Error while getting scene cards.', 'danger');
      }
    }
    if (
      submissionBoardModel.sb_status == SbStatusMaster.expired ||
      submissionBoardModel.sb_status == SbStatusMaster.locked ||
      isExpired(submissionBoardModel?.expire_at)
    ) {
      setIsSbLocked(true);
    } else {
      setIsSbLocked(false);
    }
    if (
      (submissionBoardModel?.id ?? 0) > 0 &&
      submissionBoardModel.multiple_hire > 0
    ) {
      setis_multiple_hire(true);
    } else {
      setIsSbLocked(false);
    }

    fetchRecipientForSB();
    fetchProjectMembers();
    fetchProjectOwners();
    fetchProjectSceneCards();
  }, [project]);

  const handleSearchForParticipants = useCallback(
    debounce(async (searchTerm) => {
      console.log('recipientMembers', recipientMembers);
      const projectMembersWithoutCurrentUser = recipientMembers.filter(
        ({ email }) => email !== currentUser.email
      );
      const filteredMembers = filterProjectMembersBySearchTerm(
        projectMembersWithoutCurrentUser,
        searchTerm
      );
      let uniqueFilteredMembers = filteredMembers.filter(
        ({ email }) => !selectedParticipants.some((participant) => participant.email === email)
      ); // remove already selected participants
      //syca-check get users those matches with searchTerm
      if (uniqueFilteredMembers.length <= 0 && searchTerm.length >= 3) {
        const users = await userService.getUserDirectoryAll(1, 12, {
          keyword: searchTerm
        }, '');
        if (users) {
          uniqueFilteredMembers = users.map((item) => {
            return {
              id: item.id,
              name: item.firstName + ' ' + item.lastName,
              email: item.email,
              action: 0,
            }
          })
          // id: number;
          // name?: string;
          // email: string;
          // role?: string;
          // imageUrl?: string;
          // sb_recipient_id?: number;
          // action?: number;

        }
      }
      setSearchResults(uniqueFilteredMembers);
    }, 200),
    [recipientMembers, selectedParticipants, currentUser.email]
  );

  const handleNewParticipant = (participant) => {
    if (!participant) return;
    if (selectedParticipants.some((selected) => selected.email === participant.email && selected.action !== 2)) {
      return;
    }
    let recipient = previousRecipients.find((user) => user.email === participant.email);
    if (recipient) {
      recipient.action = 0;
      setSelectedParticipants((current) => [...current, ...[recipient]]);
    } else {
      participant.action = 1;
      setSelectedParticipants((current) => [...current, ...[participant]]);
    }
  };

  const handleRemoveParticipant = (participantToRemove) => {
    if (submissionBoardModel.id == undefined || submissionBoardModel.id <= 0) {
      // remove not added
      setSelectedParticipants((current) =>
        current.filter((participant) => participant.email !== participantToRemove.email)
      );
    } else {
      // edit
      setSelectedParticipants((prevRecords) => {
        const index = prevRecords.findIndex((user) => user.email === participantToRemove.email);

        if (index !== -1) {
          // Email exists → update
          const newUsers = [...prevRecords];
          participantToRemove.action = 2;
          newUsers[index] = { ...prevRecords[index], ...participantToRemove };
          return newUsers;
        } else {
          // Email doesn't exist → optionally add
          return [...prevRecords, participantToRemove];
        }
      });
    }
  };

  const getSelectedSbStatus = () => {
    const sb_status = getValues().sb_status;
    const itemSelected = SbStatusMasterKeyValue.find((item) => item.id == sb_status);
    if (itemSelected) {
      return itemSelected;
    }
    return {
      id: '',
      name: '',
    };
  };
  const get_project_folder_uuid = () => {
    if (projectEpisodes) {
      const project_folder_uuid = getValues().project_folder_uuid;
      const itemSelected = projectEpisodes.find(
        (item) => item.id.toString() == project_folder_uuid
      );
      if (itemSelected) {
        return itemSelected;
      }
    }
    return {
      id: '',
      name: '',
    };
  };
  const get_scene_card_uuid = () => {
    if (projectSceneCards) {
      const scene_card_uuid = getValues().scene_card_uuid;
      const itemSelected = projectSceneCards.find((item) => item.id.toString() == scene_card_uuid);
      if (itemSelected) {
        return itemSelected;
      }
    }
    return {
      id: '',
      name: '',
    };
  };
  // scroll indicator

  const scrollContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Function to check for overflow
  const checkForOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current;
      // Show indicator if scrollHeight is greater than clientHeight (content overflows)
      // And if it's not already scrolled to the very bottom
      setShowScrollIndicator(scrollHeight > clientHeight && scrollContainerRef.current.scrollTop < scrollHeight - clientHeight - 5); // -5 for a small buffer
    }
  };

  useEffect(() => {
    // Initial check and re-check on resize/content change
    checkForOverflow();

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkForOverflow);
      window.addEventListener('resize', checkForOverflow);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkForOverflow);
        window.removeEventListener('resize', checkForOverflow);
      }
    };
  }, []); // Re-run effect if content changes
  // end scroll indicator
  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => onCloseDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-customGray" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto px-4 sm:px-0">
            <div className="flex min-h-full items-center justify-center md:p-8 mt-10 text-left">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                {/* <Dialog.Panel className="relative flex flex-col overflow-hidden bg-white rounded-md shadow-xl-0-19-40-8 "></Dialog.Panel> */}
                <Dialog.Panel className="max-w-3xl pt-4 w-full relative flex flex-col h-full overflow-hidden bg-white rounded-md shadow-xl-0-19-40-8 ">
                  {/* <Dialog.Panel className="max-w-3xl w-full h-800px transform bg-white p-4 text-left align-middle rounded-md shadow-xl-0-19-40-8 transition-all"> */}
                  <form
                    className="relative flex flex-col gap-4"
                    action="POST"
                    onSubmit={handleSubmit(handleFormSubmit)}
                  >
                    <div className="py-5 px-5 sm:px-6">
                      <div className="flex items-center justify-between sm:px-4 pb-4">
                        {isHeaderEdit ? (
                          <>
                            {' '}
                            <input
                              onBlur={() => {
                                console.log('onblur');
                                setIsHeaderEdit(false);
                              }}
                              onKeyDown={handleKeyDown}
                              // ref={inputHeaderRef}
                              type="text"
                              name="header"
                              id="header"
                              placeholder="header"
                              className="w-full flex-grow bg-white shadow-sm border border-gray-300 px-3 py-2 border-0 rounded-lg focus:shadow-outline text-left "
                              {...register('header', {
                                onBlur: () => {
                                  // This onBlur will fire when the input loses focus,
                                  // but not if a button with e.preventDefault() is clicked.
                                  console.log('onBlur event fired');
                                  setIsHeaderEdit(false);
                                },
                              })}
                            />
                          </>
                        ) : (
                          <div className="flex">
                            <h2
                              onClick={() => {
                                setIsHeaderEdit(true);
                              }}
                              className="text-lg font-medium text-gray-500 underline underline-offset-8"
                            >
                              {getValues().header?.trim()
                                ? getValues().header
                                : 'Untitled Submission Board'}
                            </h2>
                            <button
                              onClick={() => {
                                setIsHeaderEdit(true);
                              }}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Edit submission board header"
                            >
                              <PencilLineIcon className="h-4 w-4 text-gray-500 underline" />
                            </button>
                          </div>
                        )}



                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-md bg-white"
                          onClick={() => onCloseDialog(false)}
                        >
                          <span className="sr-only">Close menu</span>
                          <XIcon className="h-8 w-8" aria-hidden="true" />
                        </button>
                      </div>

                      <div
                        className="mt-1 justify-items-center flex max-h-full"
                        style={{ maxHeight: '80vh' }}
                      >
                        <div
                          ref={scrollContainerRef} className="gap-1 flex flex-col py-4 sm:p-4 bg-white w-full max-h-full scrollable-content  overflow-y-scroll gap-5 pb-20 border-b border-gray-300">
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex flex-col w-full sm:w-1/4">
                              <div className="flex items-center gap-1 mb-1">
                                <label htmlFor="owner_id" className="block text-sm font-medium text-gray-700">
                                  Send from*
                                </label>
                                <div className="group relative flex items-center z-50">
                                  <Tooltip message="This name will appear as the sender on every email and notification." />
                                </div>
                              </div>
                              <SelectComboboxSimple
                                name="owner_id"
                                control={control}
                                options={projectOwners}
                                placeholder="Select Owner"
                                defaultValue={getValues().owner_id || currentUser.id}
                              />
                            </div>
                            <div className=" flex flex-col w-full sm:w-1/2">
                              <label
                                htmlFor="title"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Role Name*
                              </label>
                              <input
                                type="text"
                                name="title"
                                id="title"
                                placeholder="Enter a short role description here..."
                                className="w-full flex-grow bg-white shadow-sm border border-gray-300 px-3 py-2 border-0 rounded-lg focus:shadow-outline text-left"
                                {...register('title', {
                                  maxLength: {
                                    value: 100,
                                    message: 'Board Name cannot exceed 100 characters',
                                  },
                                  required: 'Title is required. Max 100 characters.',
                                })}
                              />
                              {errors.title && (
                                <p className="text-red-900">{errors?.title?.message}</p>
                              )}
                            </div>
                            <div className="flex flex-col w-full sm:w-1/4">
                              <label
                                htmlFor="expired_at"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Expires On
                              </label>
                              <div className="relative w-full">
                                {/* Custom Calendar Icon */}
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-calendar-days-icon lucide-calendar-days absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer"
                                  onClick={() => {
                                    const input = document.getElementById("expire_at") as HTMLInputElement;
                                    input.showPicker?.();
                                  }}
                                >
                                  <path d="M8 2v4" />
                                  <path d="M16 2v4" />
                                  <rect width="18" height="18" x="3" y="4" rx="2" />
                                  <path d="M3 10h18" />
                                  <path d="M8 14h.01" />
                                  <path d="M12 14h.01" />
                                  <path d="M16 14h.01" />
                                  <path d="M8 18h.01" />
                                  <path d="M12 18h.01" />
                                  <path d="M16 18h.01" />
                                </svg>
                                <input
                                  type="date"
                                  id="expire_at"
                                  name="expire_at"
                                  min={getTodayDateString()}
                                  className="
                                  w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2
                                  focus:outline-none focus:ring-2 focus:ring-blue-500
                                  appearance-none
                                  [&::-webkit-inner-spin-button]:appearance-none
                                  [&::-webkit-clear-button]:appearance-none
                                "
                                  style={{
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                  }}
                                  {...register("expire_at", {
                                    required: "Expire at is required",
                                    validate: (value) => {
                                      const inputDate = new Date(value);
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const tomorrow = new Date(
                                        today.getFullYear(),
                                        today.getMonth(),
                                        today.getDate() + 1
                                      );
                                      return inputDate >= tomorrow || "Date must be in the future";
                                    },
                                  })}
                                />
                              </div>

                              {errors.expire_at && (
                                <p className="text-red-900">{errors.expire_at.message}</p>
                              )}
                            </div>
                          </div>
                          <div className=""></div>
                          <div className="flex flex-col sm:flex-row  gap-2">
                            {project.project_type == 'episodic' && (
                              <div className=" flex flex-col w-full">
                                <label
                                  htmlFor="title"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Episode / Block
                                </label>
                                <SelectCombobox
                                  id="project_folder_uuid"
                                  options={projectEpisodes}
                                  // initialValue={getSelectedGenders()}
                                  placeholder="Any"
                                  onChange={function (selectedValue: OptionProps): void {
                                    console.log('value:', selectedValue);
                                    setValue('project_folder_uuid', selectedValue.id.toString());
                                    // handleGenderChange(selectedValue.id);
                                  }}
                                  initialValue={get_project_folder_uuid()}
                                ></SelectCombobox>
                              </div>
                            )}
                            <div className="flex flex-col w-full">
                              <label
                                htmlFor="title"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Attach Scene Card
                              </label>
                              <div>
                                <SelectCombobox
                                  id="scene_card_uuid"
                                  options={projectSceneCards}
                                  placeholder="Search for a Scene Card"
                                  showFilmIcon={true}
                                  onChange={function (selectedValue: OptionProps): void {
                                    console.log('value:', selectedValue);
                                    setValue('scene_card_uuid', selectedValue.id.toString());
                                  }}
                                  initialValue={get_scene_card_uuid()}
                                ></SelectCombobox>
                                {/* <SelectSceneCardCombobox
                                projectSceneCards={projectSceneCards}
                                handleAttachSceneCard={(sceneCard) =>
                                  setValue('sceneCardId', sceneCard.uuid)
                                }
                                removeSceneCard={() => setValue('sceneCardId', '')}
                                currentSceneCardId={watch('sceneCardId')}
                              /> */}
                              </div>
                            </div>
                          </div>{' '}
                          <div className=""></div>
                          <div className="text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex flex-col mb-1">
                                <label htmlFor="requirements" className="flex items-center mb-1 gap-2-1">
                                  <div>Requirements*</div>
                                  <Tooltip message="Use this section to enter skills, qualifications, role specifics, or notes to share with your team and decision-makers during candidate review." />
                                </label>

                                {/* <div className="flex  gap-1">
                              <button
                                type="button"
                                onClick={handleUndo}
                                disabled={!canUndo}
                                className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                                  !canUndo ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                title="Undo"
                              >
                                <Undo className="w-4 h-4 text-gray-600" />
                              </button>
                              <span className="text-gray-400">|</span>
                              <button
                                type="button"
                                onClick={handleRedo}
                                disabled={!canRedo}
                                className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                                  !canRedo ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                title="Redo"
                              >
                                <Redo className="w-4 h-4 text-gray-600" />
                              </button>
                            </div> */}
                              </div>

                            </div>
                            <textarea
                              id={'requirements'}
                              name={'requirements'}
                              rows={4}
                              className="outline-none block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 focus:ring-2 focus:ring-inset focus:ring-blue-500"
                              placeholder={'Enter Description here...'}
                              {...register('requirements', {
                                maxLength: {
                                  value: 512,
                                  message: 'Requirements cannot exceed 512 characters',
                                },
                                required: 'Requirements is required. Max 512 characters.',
                                onChange: (e) => {
                                  handleRequirementsChange(e.target.value);
                                },
                              })}
                            />
                            {/* <TrixEditor
                            id="requirements"
                            name="requirements"
                            register={register}
                            setValue={setValue}
                            defaultValue={submissionBoardModel.requirements?.body || ''}
                            {...register('requirements', {
                              validate: (value) => {
                                console.log('validate value', value);
                                const value1 = value
                                  .replace(/<[^>]*>/g, '')
                                  .replace(/&nbsp;/g, '')
                                  .replace(/[\n\r]/g, '');
                                console.log('validate value1', value1);
                                return (
                                  value1.length <= 512 ||
                                  'Requirements cannot exceed 512 characters'
                                );
                              },
                              // maxLength: {
                              //   value: 512,
                              //   message: 'Requirements cannot exceed 512 characters',
                              // },
                              required: 'Requirements is required. Max 512 characters.',
                            })}
                          /> */}
                            {errors?.requirements && (
                              <p className="text-red-900">{String(errors?.requirements?.message)}</p>
                            )}
                          </div>
                          {submissionBoardModel.id !== undefined && submissionBoardModel.id > 0 && (
                            <div className="flex grid grid-cols-1 sm:grid-cols-2">
                              <div className="">
                                <label
                                  htmlFor="sb_status_id"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Status
                                </label>
                                <SelectCombobox
                                  id="sb_status_id"
                                  options={SbStatusMasterKeyValue}
                                  initialValue={getSelectedSbStatus()}
                                  onChange={function (selectedValue: OptionProps): void {
                                    console.log('value:', selectedValue);
                                    setValue('sb_status', selectedValue.id.toString());
                                  }}
                                ></SelectCombobox>
                              </div>
                            </div>
                          )}
                          <div className="">
                            <div className="flex gap-1 items-center">
                              <label className={`block text-sm font-medium ${(submissionBoardModel.total_profiles || 0) > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                                Selectors/Recipients
                              </label>
                              <Tooltip message="Add directors, producers, or assistants who need visibility into this board. They can review and select performers but cannot edit. Super Coordinators, Coordinator Admins, and Stunt Office Coordinators already have full access." />
                            </div>
                            <div className="border-b pb-4">
                              {!isSbLocked ? (
                                <MultiSelectInput
                                  participants={searchResults}
                                  selectedParticipants={selectedParticipants.filter(
                                    (participant) => participant.action !== 2
                                  )}
                                  onSearch={handleSearchForParticipants}
                                  onAddParticipant={handleNewParticipant}
                                  onRemoveParticipant={handleRemoveParticipant}
                                  placeholder={(submissionBoardModel.total_profiles || 0) > 0 ? "Search and add recipients..." : "Add profiles to the board before sharing it for consideration"}
                                  disabled={(submissionBoardModel.total_profiles || 0) > 0 ? false : true}
                                  className="mt-2"
                                />
                              ) : (
                                <div className="mt-2 flex flex-col gap-1">
                                  {selectedParticipants.map((user, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center rounded-md px-3 py-2 border border-gray-300"
                                    >
                                      <div className="flex-grow min-w-0 mr-3">
                                        <ParticipantItem
                                          imageUrl={user.imageUrl}
                                          name={user.name}
                                          email={user.email}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center"
                            style={{
                              minHeight: '2rem'
                            }}                        >
                            <div className="flex gap-2 items-center"
                            >
                              <input
                                id={`is_multiple_hire`}
                                name={`is_multiple_hire`}
                                type="checkbox"
                                checked={is_multiple_hire}
                                className="flex-grow bg-white shadow-sm border border-gray-300  border-0 focus:shadow-outline text-left cursor-pointer"
                                onChange={function (selectedValue: any): void {
                                  if (selectedValue.currentTarget.checked) {
                                    setis_multiple_hire(true);
                                    if (getValues().multiple_hire <= 2) {
                                      setValue('multiple_hire', 2);
                                    }
                                  } else {
                                    setis_multiple_hire(false);
                                    setValue('multiple_hire', 0);
                                  }
                                }}
                              />
                              <div className="flex gap-1 items-center">
                                <label
                                  htmlFor="is_multiple_hire"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Multiple Hire?
                                </label>
                                <Tooltip message="Enables selection of multiple performers from this submission rather than a single hire. Ideal when you need several people for one scene." />
                              </div>
                              {is_multiple_hire && (
                                <div className="flex flex-row items-center gap-2">
                                  <label
                                    htmlFor="expired_at"
                                    className="block text-sm font-medium text-gray-700"
                                  >
                                  </label>
                                  <div className="relative flex items-center border border-gray-300 rounded-lg bg-white">
                                    <input
                                      type="number"
                                      name="multiple_hire"
                                      id="multiple_hire"
                                      placeholder="multiple hire"
                                      min={2}
                                      className="w-14 px-3 py-0.5 border-0 rounded-lg focus:shadow-outline text-left focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:hidden [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:hidden"
                                      style={{
                                        MozAppearance: 'textfield',
                                        WebkitAppearance: 'textfield',
                                      }}
                                      {...register('multiple_hire', {
                                        validate: (value) => {
                                          console.log('validate value', value);
                                          return value >= 2 || 'Please enter min 2';
                                        },
                                      })}
                                    />
                                    <div className="flex flex-col border-l border-gray-300">
                                      <button
                                        type="button"
                                        onClick={handleIncrement}
                                        className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center cursor-pointer"
                                      >
                                        <ChevronUp className="w-3 h-3 text-gray-600" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleDecrement}
                                        className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center cursor-pointer"
                                      >
                                        <ChevronDown className="w-3 h-3 text-gray-600" />
                                      </button>
                                    </div>
                                  </div>
                                  {errors.multiple_hire && (
                                    <p className="text-red-900">{errors.multiple_hire.message}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex ">
                            <div className="flex gap-2 items-center">
                              <input
                                id={`is_recipient_otp`}
                                name={`is_recipient_otp`}
                                type="checkbox"
                                className="flex-grow bg-white shadow-sm border border-gray-300  border-0 focus:shadow-outline text-left cursor-pointer"
                                {...register('is_recipient_otp', {})}
                              />
                              <div className="flex relative group gap-1 items-center">
                                <label
                                  htmlFor="is_recipient_otp"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Non Filmik user, OTP required to view submission board?
                                  <span className="relative pl-1"> <Tooltip
                                    width=" w-48 "
                                    message="If the recipient is not part of the FILMIK community and this option is checked, they must enter a one-time password (sent by email) to access the board."
                                  /></span>
                                </label>

                              </div>
                            </div>
                          </div>
                          <div className=" text-center pt-4 flex flex-row justify-between items-center">
                            {/* Delete button on left - only for existing boards */}
                            {submissionBoardModel.id > 0 ? (
                              <Button
                                id="delete_sbadd_btn_id"
                                variant="secondary"
                                btn_size="small"
                                icon_position="hor"
                                onClick={handleDeleteSubmissionBoard}
                                disabled={isDeleting}
                                className="text-red-500 border-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                <span className="truncate" style={{ maxWidth: '10rem' }}>
                                  {isDeleting ? 'Deleting...' : 'Delete'}
                                </span>
                              </Button>
                            ) : (
                              <div></div>
                            )}

                            {/* Cancel and Update buttons on right */}
                            <div className="flex gap-2">
                              <Button
                                id="cancel_sbadd_btn_id"
                                variant="secondary"
                                btn_size="small"
                                icon_position="hor"
                                onClick={() => {
                                  onCloseDialog(false);
                                }}
                              >
                                <span className="truncate" style={{ maxWidth: '10rem' }}>
                                  Cancel
                                </span>
                              </Button>
                              <Button
                                id="submit_sbadd_btn_id"
                                variant="primary"
                                btn_size="small"
                                icon_position="hor"
                                type="submit"
                              >
                                <span className="truncate" style={{ maxWidth: '10rem' }}>
                                  {submissionBoardModel.id <= 0 ? 'Create' : 'Update'}
                                </span>
                              </Button>
                            </div>
                          </div>
                        </div>
                        {showScrollIndicator && (
                          <div
                            className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none flex items-end justify-center pb-2 z-10"
                            style={{
                              // Gradient for a fade-out effect
                              background: 'linear-gradient(to top, rgba(255,255,255,1) 30%, rgba(255,255,255,0) 100%)',
                            }}
                          >
                            <ChevronDownIcon className="h-6 w-6 text-gray-400 animate-bounce" />
                          </div>
                        )}
                      </div>
                    </div>

                  </form>
                  <div className="px-5 sm:px-6 pb-5 text-center text-sm text-gray-500">
                    Submission boards will be deleted automatically 30 days after they expire.
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
           <ConfirmationDialog
            title="Delete Submission Board"
            description="Are you sure you want to delete this submission board? This action cannot be undone."
            onConfirmLabel="Delete"
            onCancelLabel="Cancel"
            onConfirmAction={confirmDeleteSubmissionBoard}
            confirmVariant="danger"
            onCancelAction={() => setShowDeleteConfirmation(false)}
            isOpen={showDeleteConfirmation}
            onClose={() => {
              setShowDeleteConfirmation(false)}}
            icon={<AlertTriangle className="h-6 w-6 text-red-600" />}
          />
        </Dialog>
      </Transition.Root >

     
    </>
  );
}